import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import Status from 'components/field/Status';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Maps from 'components/maps/Maps';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ProductBoxDimensions } from 'pages/Products/Products/ProductForm.styled';

import { OTOFLEX, STORAGE_PUBLIC } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 *
 * @author  @omerrkosar
 */

const DriverTrackForm = NiceModal.create(({ id, modalName }) => {
	const { t } = useTranslation();
	const { getLocaleDate, getLocaleDateWithTime } = useLocale();

	const [markerInstance, setMarkerInstance] = useState([]); // marker instance
	const [polylineInstance, setPolylineInstance] = useState([]); // polyline instance

	const [map, setMap] = useState(null);
	const maps = window?.google?.maps;

	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState();

	const axios = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const { showError } = useAlert();

	const formMethods = useForm({
		defaultValues: {}
	});

	const {
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const [date, setDate] = useState(dayjs().startOf('day').toISOString());

	const getRandomColor = () => {
		const letters = '0123456789ABCDEF';
		let color = '#';
		for (let i = 0; i < 6; i += 1) {
			const randomValue = new Uint32Array(1);
			window.crypto.getRandomValues(randomValue);
			color += letters[randomValue[0] % 16];
		}
		return color;
	};

	const fillForm = async () => {
		const { AdvancedMarkerElement } = await maps.importLibrary('marker');
		const { Polyline } = await maps.importLibrary('maps');
		setIsLoading(true);

		try {
			const response = await axios.post(OTOFLEX.TRACK_DRIVER, {
				userID: id,
				date: date ?? dayjs().startOf('day').toISOString()
			});
			const { data } = response;
			setFormData(data);

			if (data?.data) {
				let path = [];
				const paths = [];
				const colors = [];

				data?.data.forEach((point, index) => {
					if (point.onDuty) {
						let color;
						if (!path.length) {
							color = getRandomColor();
							colors.push(color);
						} else {
							color = colors[colors.length - 1];
						}
						path.push({ lat: point.lat, lng: point.lng, color });

						if (data?.data[index + 1] && !data?.data[index + 1].onDuty) {
							const onDutyMarkerContent = document.createElement('div');
							onDutyMarkerContent.style.width = '30px';
							onDutyMarkerContent.style.height = '30px';
							onDutyMarkerContent.style.backgroundColor = color;
							onDutyMarkerContent.style.borderRadius = '50%';
							onDutyMarkerContent.style.opacity = '1';
							onDutyMarkerContent.style.border = '0';
							onDutyMarkerContent.style.position = 'relative';
							onDutyMarkerContent.style.bottom = '-10px';

							const onDutyValidMarker = new AdvancedMarkerElement({
								position: { lat: point.lat, lng: point.lng },
								content: onDutyMarkerContent,
								title: getLocaleDateWithTime(point.createdDate),
								map
							});
							setMarkerInstance((prev) => [...prev, onDutyValidMarker]);
						}
					} else {
						if (path.length) {
							paths.push([...path]);
							path = [];
						}

						if (
							data?.data[index + 1] &&
							data?.data[index + 1].onDuty &&
							index !== data.data.length - 1 &&
							point.lat !== data?.data[index + 1].lat &&
							point.lng !== data?.data[index + 1].lng
						) {
							const onDutyInvalidMarkerContent = document.createElement('div');
							onDutyInvalidMarkerContent.style.width = '30px';
							onDutyInvalidMarkerContent.style.height = '30px';
							onDutyInvalidMarkerContent.style.backgroundColor = 'red';
							onDutyInvalidMarkerContent.style.borderRadius = '50%';
							onDutyInvalidMarkerContent.style.opacity = '1';
							onDutyInvalidMarkerContent.style.border = '0';
							onDutyInvalidMarkerContent.style.position = 'relative';
							onDutyInvalidMarkerContent.style.bottom = '-10px';

							const onDutyInvalidMarker = new AdvancedMarkerElement({
								position: { lat: point.lat, lng: point.lng },
								content: onDutyInvalidMarkerContent,
								title: getLocaleDateWithTime(point.createdDate),
								map
							});
							setMarkerInstance((prev) => [...prev, onDutyInvalidMarker]);
						}
					}
				});

				if (path.length) paths.push(path);

				paths.forEach((path, index) => {
					const polyLine = new Polyline({
						path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
						geodesic: false,
						strokeColor: colors[index],
						strokeOpacity: 0.7,
						strokeWeight: 2,
						map
					});
					setPolylineInstance((prev) => [...prev, polyLine]);
				});

				const lastPath = paths[paths.length - 1];

				const startMarkerImage = document.createElement('img');

				startMarkerImage.src = `${STORAGE_PUBLIC}/mapview-icons/start-point.svg`;

				startMarkerImage.style.width = '30px';
				startMarkerImage.style.height = '30px';

				const startMarker = new AdvancedMarkerElement({
					position: lastPath[lastPath.length - 1],
					content: startMarkerImage,
					title: t('mapview.flex.startPoint'),
					map
				});

				const finishMarkerImage = document.createElement('img');

				finishMarkerImage.src = `${STORAGE_PUBLIC}/mapview-icons/finish-point.svg`;

				finishMarkerImage.style.width = '30px';
				finishMarkerImage.style.height = '30px';

				finishMarkerImage.classList.add('bounce');

				const finishMarker = new AdvancedMarkerElement({
					position: paths[0][0],
					content: finishMarkerImage,
					title: t('mapview.flex.finishPoint'),
					map
				});

				setMarkerInstance((prev) => [...prev, startMarker, finishMarker]);

				map.panTo(paths[0][0]);
			} else if (data?.lat && data?.lng) {
				const marker = new AdvancedMarkerElement({
					position: { lat: data?.lat, lng: data?.lng },
					map
				});
				marker.content.classList.add('bounce');
				map.panTo(marker.getPosition());
				showError({
					header: 'error.X002',
					message: ''
				});
			}
		} catch (error) {
			showError();
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (map) {
			maps.event.addListenerOnce(map, 'idle', () => {
				fillForm();
			});
		}
	}, [map]);

	useEffect(() => {
		if (map) {
			clearMap();

			fillForm();
		}
	}, [date]);

	const defaultColSpan = {
		md: 6
	};

	const clearMap = () => {
		markerInstance.forEach((marker) => {
			marker.setMap(null);
		});
		polylineInstance.forEach((polyline) => {
			polyline.setMap(null);
		});
		maps.event.trigger(map, 'idle', () => {
			fillForm();
		});
	};

	return (
		<Modal fullwidth name={modalName} className="modal-driver">
			<ModalTitle>
				<Typography variant="p1">trackDriver</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="name" isLoading={isLoading}>
						<Typography>{formData?.driverName}</Typography>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="onDuty" isLoading={isLoading}>
						<Status
							value={
								typeof formData?.onDuty === 'boolean' ? formData.onDuty.toString() : formData?.onDuty
							}
							style={{ padding: '1.2rem' }}></Status>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="latlon" isLoading={isLoading}>
						{formData?.lat && formData?.lng && (
							<ProductBoxDimensions>
								<Typography>{formData?.lat}</Typography>
								<Typography> , </Typography>
								<Typography>{formData?.lng}</Typography>
							</ProductBoxDimensions>
						)}
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="lastUpdated" isLoading={isLoading}>
						<Typography>{getLocaleDate(formData?.lastLatLonUpdated)}</Typography>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="date" isLoading={isLoading}>
						<DateInput
							name="date"
							value={date}
							setValue={(value) => {
								setDate(value);
							}}
						/>
					</FormItem>

					<div style={{ width: '100%', height: '60dvh' }}>
						<Maps setMap={setMap} />
					</div>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="close" variant="outline" onClick={() => closeModal()} />
			</ModalActions>
		</Modal>
	);
});

export default DriverTrackForm;
